import type { PublicState } from '@karozik/shared';

const CONFETTI = ['🎉', '🎊', '✨', '🎈', '💖', '⭐'];

function Rain({ items, count = 28 }: { items: string[]; count?: number }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          className="animate-float-up absolute bottom-0 text-[6vh]"
          style={{
            left: `${(i * 37) % 100}%`,
            animationDelay: `${(i % 9) * 0.45}s`,
            animationDuration: `${3.5 + (i % 5) * 0.6}s`,
            animationIterationCount: 'infinite',
            ['--drift' as string]: `${((i % 7) - 3) * 30}px`,
          }}
        >
          {items[i % items.length]}
        </span>
      ))}
    </div>
  );
}

function Banner({ emoji, text }: { emoji: string; text: string }) {
  return (
    <div className="bg-gradient-neon animate-pop absolute top-[3vh] left-1/2 z-30 flex -translate-x-1/2 items-center gap-[1vw] rounded-full px-[2.4vw] py-[1.4vh] text-[3.2vh] font-bold whitespace-nowrap shadow-2xl">
      <span className="text-[4vh]">{emoji}</span>
      {text}
    </div>
  );
}

function Takeover({ children, rain }: { children: React.ReactNode; rain?: string[] }) {
  return (
    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-night/75 text-center backdrop-blur-md">
      {rain && <Rain items={rain} />}
      <div className="animate-pop relative">{children}</div>
    </div>
  );
}

/** Big screen treatment of the organiser's moments and the microphone. */
export function MomentOverlay({ state, now }: { state: PublicState; now: number }) {
  if (state.mic.active) {
    return (
      <Takeover>
        <div className="animate-pulse-soft text-[18vh]">🎤</div>
        <div className="font-display mt-[2vh] text-[7vh] font-bold">{state.mic.speaker ? `On écoute ${state.mic.speaker}` : 'Un peu de silence…'}</div>
        {state.mic.speaker && <div className="mt-[1vh] text-[3vh] text-ink-dim">Un peu de silence, s’il vous plaît</div>}
      </Takeover>
    );
  }
  const m = state.moment;
  if (!m) return null;
  const age = now - m.startedAt;

  switch (m.type) {
    case 'birthday':
      return age < 25_000 ? (
        <Takeover rain={['🎂', ...CONFETTI]}>
          <div className="text-[16vh]">🎂</div>
          <div className="font-display text-[9vh] leading-tight font-bold">
            Joyeux anniversaire
            <br />
            <span className="text-gradient">{m.name} !</span>
          </div>
        </Takeover>
      ) : (
        <Banner emoji="🎂" text={`Joyeux anniversaire ${m.name} !`} />
      );
    case 'slows':
      return (
        <>
          <Rain items={['💞', '💗', '💕']} count={14} />
          {age < 12_000 ? (
            <Takeover>
              <div className="text-[16vh]">💞</div>
              <div className="font-display text-[8vh] font-bold">C’est l’heure des slows</div>
              <div className="mt-[2vh] text-[3.5vh] text-ink-dim">Invitez quelqu’un à danser…</div>
            </Takeover>
          ) : (
            <Banner emoji="💞" text="Spécial slows" />
          )}
        </>
      );
    case 'collective':
      return age < 15_000 ? (
        <Takeover rain={['🕺', '💃', '🪩']}>
          <div className="text-[16vh]">🕺💃</div>
          <div className="font-display text-[8vh] font-bold">Tout le monde sur la piste !</div>
          {m.label && <div className="mt-[2vh] text-[4vh] text-gradient">{m.label}</div>}
        </Takeover>
      ) : (
        <Banner emoji="🕺" text="Danse collective !" />
      );
    case 'photo': {
      const left = Math.ceil((m.endsAt - now) / 1000);
      return (
        <Takeover>
          {left > 3 ? (
            <>
              <div className="text-[14vh]">📸</div>
              <div className="font-display text-[8vh] font-bold">Photo de groupe !</div>
              <div className="mt-[2vh] text-[4vh] text-ink-dim">Tout le monde se rapproche…</div>
            </>
          ) : left > 0 ? (
            <div key={left} className="font-display animate-pop text-[40vh] leading-none font-bold text-gradient">
              {left}
            </div>
          ) : (
            <div className="font-display text-[16vh] font-bold">Cheese ! 😁</div>
          )}
        </Takeover>
      );
    }
    case 'lastdance':
      return <Banner emoji="🌙" text="Dernière danse…" />;
    case 'announce':
      return (
        <Takeover>
          <div className="text-[12vh]">📣</div>
          <div className="font-display max-w-[80vw] text-[7vh] leading-tight font-bold">{m.text}</div>
        </Takeover>
      );
  }
}
